import { useState } from 'react';

import SearchBox from '@/components/shared/SearchBox/SearchBox';
import type { SearchResult } from '@/components/shared/SearchBox/types';
import { useDebounce } from '@/hooks/useDebounce';
import useAppointmentStore from '@/store/AppointmentStore';
import { useLocationSearch } from '../api/getLocationSearch';
import { formatLocationTitle } from '../utils/locationSearchFormatting';

const SEARCH_DEBOUNCE_DELAY_MS = 500;

interface LocationSearchBoxProps {
  placeholder?: string;
}

const LocationSearchBox: React.FC<LocationSearchBoxProps> = ({
  placeholder = 'Search by city or ZIP code',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY_MS);

  const agentSearchLocation = useAppointmentStore(
    (state) => state.agentSearchLocation,
  );
  const setAgentSearchLocation = useAppointmentStore(
    (state) => state.setAgentSearchLocation,
  );

  const { data: locationResults = [], isFetching } =
    useLocationSearch(debouncedSearch);

  const searchResults: SearchResult[] = locationResults.map((loc) => ({
    id: loc.id,
    title: formatLocationTitle(loc),
    icon: 'location-dot-regular',
  }));

  const handleSelect = (result: SearchResult | null) => {
    if (!result) {
      setAgentSearchLocation(undefined);
      return;
    }

    const matched = locationResults.find((loc) => loc.id === result.id);
    if (matched) {
      setAgentSearchLocation(matched);
    }
  };

  return (
    <SearchBox
      placeholder={placeholder}
      value={searchTerm}
      onChange={setSearchTerm}
      searchResults={searchResults}
      isLoading={isFetching}
      onSelect={handleSelect}
      selectedResult={
        agentSearchLocation
          ? {
              id: agentSearchLocation.id,
              title: formatLocationTitle(agentSearchLocation),
            }
          : null
      }
      hideSearchIconOnSearch={false}
      toggleResultsOnFocus={false}
      simpleSelectedResultView={true}
    />
  );
};

export default LocationSearchBox;
